import {
  RESPONSIBLE_ROLE_LABELS,
  RESPONSIBLE_USER_ROLE_NAMES,
} from '../constants';

export type UserRoleInput = {
  name?: string | null;
  description?: string | null;
};

export type UserDisplayNameInput = {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
};

export type ResponsibleUserInput = UserDisplayNameInput & {
  id?: number;
  role?: UserRoleInput | null;
};

/** GAP-061 / GAP-063: trim(firstName + ' ' + lastName) || username — never includes role */
export function formatUserDisplayName(user?: UserDisplayNameInput | null): string {
  if (!user) return '—';

  const name = [user.firstName, user.lastName]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ')
    .trim();

  if (name) return name;

  const username = user.username?.trim();
  if (username) return username;

  return '—';
}

export function formatUserRoleLabel(role?: UserRoleInput | null): string {
  const roleName = String(role?.name ?? '').toLowerCase();
  if (!roleName) return '';
  return RESPONSIBLE_ROLE_LABELS[roleName] ?? '';
}

export function formatUserDisplayLabel(
  user?: ResponsibleUserInput | null,
  options?: { includeRole?: boolean }
): string {
  const name = formatUserDisplayName(user);
  if (name === '—' || !options?.includeRole) return name;

  const roleLabel = formatUserRoleLabel(user?.role);
  if (!roleLabel) return name;

  return `${name} (${roleLabel})`;
}

export function formatResponsibleUserLabel(user?: ResponsibleUserInput | null): string {
  return formatUserDisplayLabel(user, { includeRole: true });
}

export function isResponsibleEligibleUser(user?: { role?: UserRoleInput | null } | null): boolean {
  const roleName = String(user?.role?.name ?? '').toLowerCase();
  return (RESPONSIBLE_USER_ROLE_NAMES as readonly string[]).includes(roleName);
}

export function filterResponsibleEligibleUsers<T extends { role?: UserRoleInput | null }>(
  users: T[] | null | undefined
): T[] {
  return (users ?? []).filter(isResponsibleEligibleUser);
}

/** Retain legacy assigned user in select options so Ant Design does not drop the value. */
export function mergeResponsibleSelectUsers<T extends ResponsibleUserInput>(
  users: T[] | null | undefined,
  current?: ResponsibleUserInput | null
): T[] {
  const eligible = filterResponsibleEligibleUsers(users);
  if (current?.id == null) return eligible;
  if (eligible.some((u) => u.id === current.id)) return eligible;
  return [...eligible, current as T];
}
